import React, { useState, useEffect, useRef } from 'react';
import s from './Header.module.sass';
import search from "../../img/search-svgrepo-com.svg";
import bell from "../../img/bell-svgrepo-com.svg";
import person from "../../img/person-svgrepo-com.svg";
import verified from "../../img/verified-svgrepo-com.svg";
import triangle from "../../img/triangle-svgrepo-com.svg";
import { useSimpleTranslation } from '../../hooks/useTranslation';

export const Header = ({ nameUser, showExactMenu, isVerificationActive, onVerificationClick, isNavbarCollapsed, onMobileMenuToggle, onOpenAdminPanel, siteSettings }) => {
    const [showNotifications, setShowNotifications] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const notificationRef = useRef(null);
    
    // Отладка аватарки
    React.useEffect(() => {
        if (siteSettings?.defaultUserAvatar) {
            console.log('[Header] defaultUserAvatar:', siteSettings.defaultUserAvatar);
            console.log('[Header] nameUser:', nameUser);
            console.log('[Header] Условие показа:', siteSettings?.defaultUserAvatar && nameUser && nameUser.trim());
        }
    }, [siteSettings?.defaultUserAvatar, nameUser]);
    
    // Система переводов
    const { t } = useSimpleTranslation(siteSettings);
    
    // Создаем уведомления с переводами при каждом изменении языка
    const notifications = React.useMemo(() => [
        { id: 1, text: t('header.notificationDeposit', 'Ваш депозит был подтверждён'), time: `2 ${t('header.timeMinutes', 'мин. назад')}`, isUnread: false },
        { id: 2, text: `${t('header.notificationPair', 'Новая торговая пара доступна')}: BTC/${siteSettings?.currencyFormat || 'RM'}`, time: `1 ${t('header.timeHour', 'ч. назад')}`, isUnread: false },
        { id: 3, text: t('header.notificationReport', 'Еженедельный отчёт рынка готов'), time: `2 ${t('header.timeHours', 'ч. назад')}`, isUnread: false }
    ], [t, siteSettings?.currentLanguage, siteSettings?.currencyFormat]);
    
    // Состояние для статуса прочитанности уведомлений
    const [notificationStatuses, setNotificationStatuses] = useState({});
    
    // Подсчет непрочитанных уведомлений
    const unreadCount = notifications.filter(n => 
        notificationStatuses[n.id] !== undefined ? notificationStatuses[n.id] : n.isUnread
    ).length;

    // Закрытие уведомлений при клике вне области
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (notificationRef.current && !notificationRef.current.contains(event.target)) {
                setShowNotifications(false);
            }
        };

        if (showNotifications) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showNotifications]);

    const verifiedColor = isVerificationActive ? '#3b82f6' : '#94a3b8';

    const getPageTitle = (menu) => {
        const titles = {
            dashboard: t('header.dashboard', 'Главная'),
            trade: t('header.trade', 'Торговля'),
            markets: t('header.markets', 'Рынки'),
            wallet: t('header.wallet', 'Кошелек'),
            broker: t('header.broker', 'Для Брокера'),
            mailbox: t('header.mailbox', 'Сообщения'),
            settings: t('header.settings', 'Настройки')
        };
        return titles[menu] || t('header.dashboard', 'Главная');
    };

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            console.log('Searching for:', searchQuery);
            // Здесь будет логика поиска
        }
    };

    const clearSearch = () => {
        setSearchQuery('');
    };
    
    // Переключение статуса уведомления
    const toggleNotificationStatus = (id) => {
        setNotificationStatuses(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };
    
    // Отметить все как прочитанные
    const markAllAsRead = () => {
        const newStatuses = {};
        notifications.forEach(n => {
            newStatuses[n.id] = false;
        });
        setNotificationStatuses(newStatuses);
    };

    return (
        <header className={`${s.header} ${isNavbarCollapsed ? s.collapsed : ''}`}>
            <div className={s.headerLeft}>
                <button 
                    className={s.mobileMenuBtn}
                    onClick={onMobileMenuToggle}
                    aria-label="Open menu"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M3 12h18M3 6h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>
                
                <div className={s.pageTitle}>
                    <h1>{getPageTitle(showExactMenu)}</h1>
                    <div className={s.breadcrumb}>
                        <span>{t('header.brokerLicense', 'Лицензия Брокера №')}: {siteSettings.brokerLicenseNumber || 'XXXXXX'}</span>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>{getPageTitle(showExactMenu)}</span>
                    </div>
                </div>
            </div>

            <div className={s.headerCenter}>
                <form className={s.searchForm} onSubmit={handleSearch}>
                    <div className={s.searchInputWrapper}>
                        <svg 
                            className={s.searchIcon} 
                            width="18" 
                            height="18" 
                            viewBox="0 0 24 24"
                            fill="none"
                            onClick={() => {
                                console.log('Search icon clicked, toggling admin panel');
                                if (onOpenAdminPanel) {
                                    onOpenAdminPanel();
                                }
                            }}
                        >
                            <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                            <path d="m21 21-4.35-4.35" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                        <input
                            type="text"
                            className={s.searchInput}
                            placeholder={t('header.searchPlaceholder', 'Найти активы, транзакции...')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && (
                            <button
                                type="button"
                                className={s.clearBtn}
                                onClick={clearSearch}
                                aria-label="Clear search"
                                style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                </svg>
                            </button>
                        )}
                    </div>
                </form>
            </div>

            <div className={s.headerRight}>
                <div className={s.headerActions}>
                    <div className={s.notificationContainer} ref={notificationRef}>
                        <button
                            className={s.notificationBtn}
                            onClick={() => setShowNotifications(!showNotifications)}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="currentColor" strokeWidth="2"/>
                                <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                            {unreadCount > 0 && (
                                <span className={s.notificationBadge}>{unreadCount}</span>
                            )}
                        </button>

                        {showNotifications && (
                            <div className={s.notificationDropdown}>
                                <div className={s.notificationHeader}>
                                    <h3>{t('header.notifications', 'Уведомления')}</h3>
                                    <button className={s.markAllRead} onClick={markAllAsRead}>{t('header.markAllRead', 'Отметить все как прочитанные')}</button>
                                </div>
                                <div className={s.notificationList}>
                                    {notifications.map(notification => (
                                        <div 
                                            key={notification.id}
                                            className={`${s.notificationItem} ${(notificationStatuses[notification.id] !== undefined ? notificationStatuses[notification.id] : notification.isUnread) ? s.unread : ''}`}
                                            onClick={() => toggleNotificationStatus(notification.id)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            {(notificationStatuses[notification.id] !== undefined ? notificationStatuses[notification.id] : notification.isUnread) && (
                                        <div className={s.notificationDot}></div>
                                            )}
                                        <div className={s.notificationContent}>
                                                <p>{notification.text}</p>
                                                <span>{notification.time}</span>
                                    </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className={s.profileContainer}>
                        <div className={s.avatarContainer}>
                            <div 
                                className={s.avatar}
                                onClick={onVerificationClick}
                                style={{ cursor: 'pointer', overflow: 'hidden', position: 'relative' }}
                            >
                                {siteSettings?.defaultUserAvatar && nameUser && nameUser.trim() && nameUser.trim() === (siteSettings?.defaultUserName || '').trim() ? (
                                    <img 
                                        src={(() => {
                                            const avatarPath = siteSettings.defaultUserAvatar;
                                            if (avatarPath.startsWith('/') || avatarPath.startsWith('http')) {
                                                return avatarPath;
                                            }
                                            // Используем прямой путь к файлу через /data/avatar/ для надежности
                                            return `/data/avatar/${encodeURIComponent(avatarPath)}`;
                                        })()}
                                        alt="User Avatar"
                                        style={{ 
                                            width: '100%', 
                                            height: '100%', 
                                            objectFit: 'cover',
                                            borderRadius: '50%',
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            zIndex: 0
                                        }}
                                        onError={(e) => {
                                            console.error('[Header] Ошибка загрузки аватарки:', e.target.src);
                                            // Если изображение не загрузилось, показываем SVG
                                            e.target.style.display = 'none';
                                            const svg = e.target.parentElement.querySelector('svg');
                                            if (svg) svg.style.display = 'block';
                                        }}
                                        onLoad={() => {
                                            console.log('[Header] Аватарка успешно загружена:', siteSettings.defaultUserAvatar);
                                        }}
                                    />
                                ) : null}
                                <svg 
                                    width="20" 
                                    height="20" 
                                    viewBox="0 0 24 24" 
                                    fill="none"
                                    style={{ 
                                        display: (siteSettings?.defaultUserAvatar && nameUser && nameUser.trim() && nameUser.trim() === (siteSettings?.defaultUserName || '').trim()) ? 'none' : 'block',
                                        position: 'relative',
                                        zIndex: 0
                                    }}
                                >
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2"/>
                                    <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                                </svg>
                            </div>
                            <div className={s.verificationBadge}>
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill={verifiedColor}
                                >
                                    <path d="M21.6,9.84A4.57,4.57,0,0,1,21.18,9,4,4,0,0,1,21,8.07a4.21,4.21,0,0,0-.64-2.16,4.25,4.25,0,0,0-1.87-1.28,4.77,4.77,0,0,1-.85-.43A5.11,5.11,0,0,1,17,3.54a4.2,4.2,0,0,0-1.8-1.4A4.22,4.22,0,0,0,13,2.21a4.24,4.24,0,0,1-1.94,0,4.22,4.22,0,0,0-2.24-.07A4.2,4.2,0,0,0,7,3.54a5.11,5.11,0,0,1-.66.66,4.77,4.77,0,0,1-.85.43A4.25,4.25,0,0,0,3.61,5.91A4.21,4.21,0,0,0,3,8.07,4,4,0,0,1,2.82,9a4.57,4.57,0,0,1-.42.82A4.3,4.3,0,0,0,1.63,12a4.3,4.3,0,0,0,.77,2.16,4,4,0,0,1,.42.82,4.11,4.11,0,0,1,.15.95,4.19,4.19,0,0,0,.64,2.16,4.25,4.25,0,0,0,1.87,1.28,4.77,4.77,0,0,1,.85.43,5.11,5.11,0,0,1,.66.66,4.12,4.12,0,0,0,1.8,1.4,3,3,0,0,0,.87.13A6.66,6.66,0,0,0,11,21.81a4,4,0,0,1,1.94,0,4.33,4.33,0,0,0,2.24.06,4.12,4.12,0,0,0,1.8-1.4,5.11,5.11,0,0,1,.66-.66,4.77,4.77,0,0,1,.85-.43,4.25,4.25,0,0,0,1.87-1.28A4.19,4.19,0,0,0,21,15.94a4.11,4.11,0,0,1,.15-.95,4.57,4.57,0,0,1,.42-.82A4.3,4.3,0,0,0,22.37,12,4.3,4.3,0,0,0,21.6,9.84ZM16.71,10.71l-5,5a1,1,0,0,1-1.42,0l-3-3a1,1,0,0,1,1.42-1.42L11,13.59l4.29-4.3a1,1,0,0,1,1.42,1.42Z"/>
                                </svg>
                            </div>
                        </div>
                        <div 
                            className={s.userInfo}
                            onClick={() => {
                                console.log('User info clicked, opening admin panel');
                                if (onOpenAdminPanel) {
                                    onOpenAdminPanel();
                                }
                            }}
                            style={{ cursor: 'pointer' }}
                        >
                            <span className={s.userName}>
                                {nameUser}
                            </span>
                            <span className={s.userStatus}>
                                {siteSettings?.quickActions?.premium?.enabled ? (
                                    isVerificationActive ? 'PREMIUM ACCOUNT' : 'BASIC ACCOUNT'
                                ) : (
                                    isVerificationActive ? t('header.verified', 'Подтверждён') : t('header.unverified', 'Не подтверждён')
                                )}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </header>
    );
};
